import { inject, untracked } from '@angular/core';
import { signalStore, type, withState, withMethods, patchState, withHooks } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, exhaustMap, tap, catchError, delay, retryWhen, take, timer, throwError, EMPTY, retry, Subscription, of, concatMap, Observable, forkJoin, map } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import { setPropError, setPropLoaded, setPropLoading, withReqState, setPropInit } from '@application/data-access';
import {
  addEntity,
  entityConfig,
  setEntities,
  updateEntity,
  removeEntity,
  withEntities,
  removeAllEntities,
} from '@ngrx/signals/entities';
import {
  AccessEvalResult,
  EdgeItem,
  EdgeStatus,
  HubConnectionStatus,
  AccessPoint,
  CreateAccessPointRequest,
  UpdateAccessPointRequest,
  DirectionControlEnum,
  PassagePhysicalDirectionEnum,
  AccessPointBackendTypeEnum,
  AdvertisementPositionEnum,
  convertEnumToNumber,
  GatePassage,
  ELogicalDirectionEnum,
  EvaluateByPersonIdRequest,
  AccessMethodEnum,
  AccessIntentEnum,
  ISpaceAccessPoint,
  BiometricReader,
  BiometricReaderType,
  EEdgeDeviceAction,
} from '../entities/access-edges.interface';
import { AccessPointEdgeService } from '../infra/access-point-edge.service';
import {
  AccessPointEdgeAccessEvalResultEvent,
  AccessPointEdgeGatePassageEvent,
  AccessPointEdgeNetworkInfoEvent,
  AccessPointEdgeHubService,
  ESignalQuality
} from '../infra/access-point-edge-hub.service';
import { tapResponse } from '@ngrx/operators';
import { normalizeCameraDevices } from './camera-device-normalization.util';
import { resolveWebcamsToRelease, TWebcamRelease } from './webcam-release.util';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { APP_CONFIG } from '@application/config';
import { getDateTime, normalizeId } from '@shared/utils';
import { DateTime } from 'luxon';

export type TWebcam = { Index: number; InstanceId: string; Name: string };

export type TAccessPointEdgeStore = {
  publicIp: string | null;
  hubConnectionStatus: HubConnectionStatus;
  lastReleaseByEdgeId: Record<string, GatePassage>;
  lastAccessEvalResultByEdgeId: Record<string, AccessEvalResult>;
  isWizardFlowActive: boolean;
  lastDirection: ELogicalDirectionEnum | null;
  lastEvaluateByPersonIdAccessIntent: AccessIntentEnum | null;
  /** LogicalDirection from EvaluateByPersonId response (1 = Entry, 2 = Exit). */
  lastEvaluateByPersonIdLogicalDirection: number | null;
  webcams: TWebcam[];
  biometricReaders: BiometricReader[];
  /** Webcams deactivated on their edges so the browser can open them. */
  webcamsReleasedForBrowser: TWebcamRelease[];
};

const SIGNAL_QUALITY_ONLINE = ESignalQuality[ESignalQuality.Excellent] as keyof typeof ESignalQuality;
const SIGNAL_QUALITY_OFFLINE = ESignalQuality[ESignalQuality.Offline] as keyof typeof ESignalQuality;

export const initialAccessPointEdgeStore: TAccessPointEdgeStore = {
  publicIp: null,
  hubConnectionStatus: 'disconnected',
  lastReleaseByEdgeId: {},
  lastAccessEvalResultByEdgeId: {},
  isWizardFlowActive: false,
  lastDirection: null,
  lastEvaluateByPersonIdAccessIntent: null,
  lastEvaluateByPersonIdLogicalDirection: null,
  webcams: [],
  biometricReaders: [],
  webcamsReleasedForBrowser: [],
};

const configEdge = entityConfig({
  entity: type<EdgeItem>(),
  collection: 'edges',
  selectId: (edge: EdgeItem) => edge.EdgeId
});

const configAccessPoint = entityConfig({
  entity: type<AccessPoint>(),
  collection: 'accessPoints',
  selectId: (accessPoint: AccessPoint) => accessPoint.AccessPointId
});

const configSpacesAccessPoints = entityConfig({
  entity: type<ISpaceAccessPoint>(),
  collection: 'spacesAccessPoints',
  selectId: (spacesAccessPoint: ISpaceAccessPoint) => spacesAccessPoint.SpaceId
});

export const AccessPointEdgeStore = signalStore(
  { providedIn: 'root' },
  withState<TAccessPointEdgeStore>(initialAccessPointEdgeStore),
  withEntities(configEdge),
  withEntities(configAccessPoint),
  withEntities(configSpacesAccessPoints),
  withMethods(
    (
      store,
      edgesService = inject(AccessPointEdgeService),
      errorStore = inject(ErrorStore),
      hubService = inject(AccessPointEdgeHubService),
      url = inject(APP_CONFIG).apiUrl,
      dxOdataStoreService = inject(DxOdataStoreService),
    ) => {
      
      const createSpaceAccessPoint = rxMethod<{ AccessPointId: string; SpaceId: string }>(pipe(
        tap(() => patchState(store, setPropLoading('createSpaceAccessPoint'))),
        concatMap((data) => edgesService.createSpaceAccessPoint(data).pipe(
          tapResponse({
            next: (response: ISpaceAccessPoint) => {
              patchState(store, addEntity(response, configSpacesAccessPoints), setPropLoaded('createSpaceAccessPoint'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('createSpaceAccessPoint', error));
            }
          })
        ))
      ));

    return {

      getSpacesAccessPointsByAccessPointId: (AccessPointId: string) => {
        patchState(store, setPropLoading('getSpacesAccessPointsByAccessPointId'), removeAllEntities(configSpacesAccessPoints))

        const dataSource = dxOdataStoreService.fromOdata({
          url: url.odata_access_service,
          metadata: 'SpacesAccessPoints',
          key: 'SpaceId',
          params: {
            $filter: `AccessPointId eq ${AccessPointId}`
          }
        });
        dataSource.paginate(false);
        dataSource.load().then(spaces => {
          patchState(store, setPropLoaded('getSpacesAccessPointsByAccessPointId'), setEntities(spaces, configSpacesAccessPoints));
        }).catch(error => {
          patchState(store, setPropError('getSpacesAccessPointsByAccessPointId', error));
        })
      },

      createSpaceAccessPoint,

      deleteSpaceAccessPoint: rxMethod<{ accessPointId: string; spaceId: string }>(pipe(
        tap(() => patchState(store, setPropLoading('deleteSpaceAccessPoint'))),
        concatMap(({ accessPointId, spaceId }) => edgesService.deleteSpaceAccessPoint(accessPointId, spaceId).pipe(
          tapResponse({
            next: () => {
              patchState(store, removeEntity(spaceId, configSpacesAccessPoints), setPropLoaded('deleteSpaceAccessPoint'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('deleteSpaceAccessPoint', error));
            }
          })
        ))
      )),

      getPublicIp: rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('getPublicIp'))),
        switchMap(() => edgesService.getPublicIp().pipe(
          retryWhen((errors) =>
            errors.pipe(
              delay(1000),
              take(3)
            )
          ),
          catchError(() => edgesService.getPublicIpFallback().pipe(
            retryWhen((errors) =>
              errors.pipe(
                delay(1000),
                take(2)
              )
            )
          )),
          tapResponse({
            next: (response) => {
              const ip = response?.ip || '';
              patchState(store, { publicIp: ip }, setPropLoaded('getPublicIp'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('getPublicIp', error));
            }
          })
        ))
      )),

      loadInitialDevices: rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('loadInitialDevices'))),
        switchMap(() => edgesService.loadInitialDevices().pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: (response) => {
              const devices = Array.isArray(response)
                ? response
                : Array.isArray((response as any)?.items)
                ? (response as any).items
                : [];

              if (devices.length === 0) {
                patchState(store, setPropLoaded('loadInitialDevices'));
                return;
              }

              const edges: EdgeItem[] = devices
                .map((device) => {
                  const edgeId = device.edgeId || device.EdgeId;
                  const apiKeyKind = device.apiKeyKind ?? device.ApiKeyKind;
                  if (!edgeId) return null;
                  
                  return {
                    EdgeId: edgeId,
                    Status: 'online' as EdgeStatus,
                    SignalQuality: SIGNAL_QUALITY_ONLINE,
                    LastTs: DateTime.now().toISO(),
                    ApiKeyKind: apiKeyKind,
                  };
                })
                .filter((edge): edge is EdgeItem => edge !== null);

              patchState(store, 
                setEntities(edges, configEdge),
                setPropLoaded('loadInitialDevices')
              );
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadInitialDevices', error));
            }
          })
        ))
      )),

      bindEdge: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('bindEdge'))),
        switchMap((edgeId) => edgesService.bindEdge(edgeId).pipe(
          retry({
            count: 2,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: () => {
              patchState(store, setPropLoaded('bindEdge'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('bindEdge', error));
            }
          })
        ))
      )),

      /**
       * `exhaustMap` (e não `switchMap`): o store é `providedIn: 'root'` e mais
       * de um consumidor chama este método guardando por
       * `if (!loadAccessPointsLoaded())` — por exemplo `profile-change-avatar` e,
       * logo em seguida, o `modal-capture-image-from-access` que ele abre.
       * Enquanto a primeira requisição está em voo a flag `Loaded` ainda é
       * `false`, então a segunda chamada disparava e o `switchMap` cancelava a
       * primeira: o XHR era abortado com os headers já recebidos (200 sem corpo
       * no DevTools) e a lista nunca chegava. Como a carga é sempre a mesma
       * lista global, ignorar chamadas concorrentes é o comportamento correto.
       */
      loadAccessPoints: rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('loadAccessPoints'))),
        exhaustMap(() => edgesService.getAccessPoints().pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: (response) => {
              const edges = Object.values(store.edgesEntities());
              const edgesMap = new Map(edges.map(edge => [edge.EdgeId, edge]));

              const accessPoints: AccessPoint[] = response.map((ap) => {
                /**
                 * `EdgeId` é `null` nos pontos legados, que não têm edge.
                 * Sem edge não há estado a apurar — daí `'unknown'`, distinto
                 * do `'offline'` de um edge conhecido que não respondeu.
                 *
                 * A presença continua a ser decidida pelo seed do
                 * `/v1/edge-manager` (`edgesEntities`) e corrigida em tempo real
                 * pelo hub; `EdgeIsActive` não entra aqui porque é flag de
                 * configuração, não batimento.
                 */
                const edgeId = ap.EdgeId ?? '';
                const status: EdgeStatus = edgeId
                  ? (edgesMap.get(edgeId)?.Status ?? 'offline')
                  : 'unknown';

                return {
                  AccessPointId: ap.AccessPointId,
                  EquipmentId: ap.EquipmentId,
                  Name: ap.Name,
                  IsActive: ap.IsActive,
                  BackendTypeEnum: ap.BackendTypeEnum,
                  CameraDevices: normalizeCameraDevices(ap.CameraDevices),
                  EntryMode: ap.EntryMode,
                  ExitMode: ap.ExitMode,
                  EntryDirection: ap.EntryDirection,
                  IsSplitScreenMode: ap.IsSplitScreenMode,
                  ShowAdvertisement: ap.ShowAdvertisement,
                  AdvertisementPosition: ap.AdvertisementPosition,
                  ReleaseDurationSeconds: ap.ReleaseDurationSeconds ?? 1,
                  GateNumber: ap.GateNumber,
                  EdgeId: edgeId,
                  BoardType: ap.BoardType,
                  Status: status,
                  IsLegacy: ap.IsLegacy,
                  SignalQuality: status === 'online' ? SIGNAL_QUALITY_ONLINE : SIGNAL_QUALITY_OFFLINE,
                  LastTs: ap.EdgeLastSeenUtc || DateTime.now().toISO(),
                  EdgeLastSeenUtc: ap.EdgeLastSeenUtc ?? null,
                  // The OData list writes the reader's type by name ("DigitalPersona"),
                  // as it writes the cameras': the number goes in here, at the same border.
                  BiometricReader: ap.BiometricReader
                    ? {
                        ...ap.BiometricReader,
                        Type: convertEnumToNumber(BiometricReaderType, ap.BiometricReader.Type) as BiometricReaderType,
                      }
                    : ap.BiometricReader,
                };
              }).filter(ap => ap.AccessPointId && ap.AccessPointId.trim() !== '');

              patchState(store,
                setEntities(accessPoints, configAccessPoint),
                setPropLoaded('loadAccessPoints')
              );
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadAccessPoints', error));
            }
          })
        ))
      )),

      createAccessPointInitial: rxMethod<{ edgeId: string; spaces: ISpaceAccessPoint[]}>(pipe(
        tap(() => patchState(store, setPropLoading('createAccessPointInitial'))),
        switchMap(({ edgeId, spaces }) => {
          const request: CreateAccessPointRequest = {
            EquipmentId: null,
            Name: 'Novo ponto de acesso',
            IsActive: true,
            BackendTypeEnum: 0,
            CameraDevices: [],
            EntryMode: DirectionControlEnum.Free,
            ExitMode: DirectionControlEnum.Free,
            EntryDirection: PassagePhysicalDirectionEnum.Clockwise,
            ReleaseDurationSeconds: 5,
            IsSplitScreenMode: true,
            ShowAdvertisement: true,
          };

          return edgesService.createAccessPoint(request).pipe(
            retry({
              count: 3,
              delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
            }),
            tapResponse({
              next: (response) => {
                const backendTypeEnum = convertEnumToNumber(AccessPointBackendTypeEnum, response.BackendTypeEnum) ?? 0;
                const entryMode = convertEnumToNumber(DirectionControlEnum, response.EntryMode) ?? DirectionControlEnum.Free;
                const exitMode = convertEnumToNumber(DirectionControlEnum, response.ExitMode) ?? DirectionControlEnum.Free;
                const entryDirection = convertEnumToNumber(PassagePhysicalDirectionEnum, response.EntryDirection) ?? PassagePhysicalDirectionEnum.Clockwise;

                const accessPoint: AccessPoint = {
                  AccessPointId: response.AccessPointId,
                  EquipmentId: response.EquipmentId,
                  Name: response.Name,
                  IsActive: response.IsActive,
                  BackendTypeEnum: backendTypeEnum,
                  CameraDevices: normalizeCameraDevices(response.CameraDevices),
                  EntryMode: entryMode,
                  ExitMode: exitMode,
                  EntryDirection: entryDirection,
                  IsSplitScreenMode: response.IsSplitScreenMode,
                  ShowAdvertisement: response.ShowAdvertisement,
                  AdvertisementPosition: convertEnumToNumber(AdvertisementPositionEnum, response.AdvertisementPosition) ?? AdvertisementPositionEnum.Top,
                  ReleaseDurationSeconds: response.ReleaseDurationSeconds ?? 1,
                  GateNumber: response.GateNumber,
                  EdgeId: edgeId,
                  Status: 'unknown' as EdgeStatus,
                  SignalQuality: SIGNAL_QUALITY_OFFLINE,
                  LastTs: DateTime.now().toISO(),
                  BiometricReader: response.BiometricReader ?? null,
                };

                spaces?.forEach((space) => {
                  const SpaceId = normalizeId(space?.SpaceId);
                  if (SpaceId) createSpaceAccessPoint({ SpaceId, AccessPointId: accessPoint.AccessPointId });
                });

                patchState(store,
                  addEntity(accessPoint, configAccessPoint),
                  setPropLoaded('createAccessPointInitial')
                );
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('createAccessPointInitial', error));
              }
            })
          );
        })
      )),

      updateAccessPoint: rxMethod<{ data: UpdateAccessPointRequest }>(pipe(
        tap(() => patchState(store, setPropLoading('updateAccessPoint'))),
        switchMap(({ data }) => edgesService.updateAccessPoint(data).pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: (response) => {
              const existing = store.accessPointsEntityMap()[data.AccessPointId];
              if (existing) {
                const backendTypeEnum = response.BackendTypeEnum !== undefined 
                  ? (convertEnumToNumber(AccessPointBackendTypeEnum, response.BackendTypeEnum) ?? existing.BackendTypeEnum)
                  : existing.BackendTypeEnum;
                const entryMode = response.EntryMode !== undefined
                  ? (convertEnumToNumber(DirectionControlEnum, response.EntryMode) ?? existing.EntryMode)
                  : existing.EntryMode;
                const exitMode = response.ExitMode !== undefined
                  ? (convertEnumToNumber(DirectionControlEnum, response.ExitMode) ?? existing.ExitMode)
                  : existing.ExitMode;
                const entryDirection = response.EntryDirection !== undefined
                  ? (convertEnumToNumber(PassagePhysicalDirectionEnum, response.EntryDirection) ?? existing.EntryDirection)
                  : existing.EntryDirection;

                const updated: AccessPoint = {
                  ...existing,
                  AccessPointId: response.AccessPointId,
                  EquipmentId: response.EquipmentId ?? existing.EquipmentId,
                  Name: response.Name ?? existing.Name,
                  IsActive: response.IsActive ?? existing.IsActive,
                  BackendTypeEnum: backendTypeEnum,
                  CameraDevices: response.CameraDevices !== undefined
                    ? normalizeCameraDevices(response.CameraDevices)
                    : existing.CameraDevices,
                  EntryMode: entryMode,
                  ExitMode: exitMode,
                  EntryDirection: entryDirection,
                  IsSplitScreenMode: response.IsSplitScreenMode ?? existing.IsSplitScreenMode,
                  ShowAdvertisement: response.ShowAdvertisement ?? existing.ShowAdvertisement,
                  AdvertisementPosition: convertEnumToNumber(AdvertisementPositionEnum, response.AdvertisementPosition) ?? existing.AdvertisementPosition,
                  ReleaseDurationSeconds: response.ReleaseDurationSeconds ?? existing.ReleaseDurationSeconds,
                  GateNumber: response.GateNumber ?? existing.GateNumber,
                  // Use explicit undefined check so that response.BiometricReader === null
                  // (operator cleared the reader) overwrites existing, instead of being
                  // collapsed into "no change" by ?? operator.
                  BiometricReader: response.BiometricReader !== undefined
                    ? response.BiometricReader
                    : existing.BiometricReader,
                };

                patchState(store,
                  updateEntity({ id: data.AccessPointId, changes: updated }, configAccessPoint),
                  setPropLoaded('updateAccessPoint')
                );
              } else {  
                patchState(store, setPropLoaded('updateAccessPoint'));
              }
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('updateAccessPoint', error));
            }
          })
        ))
      )),

      loadBiometricReaders: rxMethod<string>(pipe(
        tap(() => patchState(store, { biometricReaders: [] }, setPropLoading('loadBiometricReaders'))),
        switchMap((edgeId) => edgesService.loadBiometricReaders(edgeId).pipe(
          tapResponse({
            next: (response) => {
              const readers = Array.isArray(response) ? response : [];
              patchState(store, { biometricReaders: readers }, setPropLoaded('loadBiometricReaders'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadBiometricReaders', error));
            }
          })
        ))
      )),

      resetBiometricReaders: () => {
        patchState(store, { biometricReaders: [] }, setPropInit('loadBiometricReaders'));
      },

      configureDevice: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('configureDevice'))),
        switchMap((edgeId) => {
          return edgesService.bindEdge(edgeId).pipe(
            switchMap(() => of(edgeId)),
            catchError((error: HttpErrorResponse) => {
              const isAlreadyBound = error.status === 409 || 
                                    (error.status === 400 && error.error?.toString().toLowerCase().includes('already'));

              const isAlreadyBoundOtherTenant = error.status === 400 && error.error?.toString() === 'Edge already bound other tenant';       

              if (isAlreadyBound && !isAlreadyBoundOtherTenant) {
                return of(edgeId);
              } else {
                if (isAlreadyBoundOtherTenant) {
                  errorStore.handleError({ error, message: 'SETTINGS.ACCESS.EDGES.ALERT.CONFIGURE.ERROR.MESSAGE_ANOTHER_TENANT' });
                } else {
                  errorStore.setHttpError(error);
                }
                patchState(store, setPropError('bindEdge', error));
                patchState(store, setPropError('configureDevice', error));
                return EMPTY;
              }
            })
          );
        }),
        switchMap((edgeId) => edgesService.getByEdgeId(edgeId).pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          concatMap((equipmentResponse) => {

            if (!equipmentResponse) {
              return throwError(() => new Error('Equipment not found'));
            }

            const createData: CreateAccessPointRequest = {
              EquipmentId: equipmentResponse.EquipmentId,
              Name: equipmentResponse?.Name || 'Novo ponto de acesso',
              IsActive: true,
              BackendTypeEnum: 0,
              CameraDevices: [],
              EntryMode: DirectionControlEnum.Free,
              ExitMode: DirectionControlEnum.Free,
              EntryDirection: PassagePhysicalDirectionEnum.Clockwise,
              ReleaseDurationSeconds: 5,
              IsSplitScreenMode: true,
              ShowAdvertisement: true,
            };

            return edgesService.createAccessPoint(createData).pipe(
              retry({
                count: 3,
                delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
              }),
              tapResponse({
                next: (response) => {
                  const backendTypeEnum = convertEnumToNumber(AccessPointBackendTypeEnum, response.BackendTypeEnum) ?? 0;
                  const entryMode = convertEnumToNumber(DirectionControlEnum, response.EntryMode) ?? DirectionControlEnum.Free;
                  const exitMode = convertEnumToNumber(DirectionControlEnum, response.ExitMode) ?? DirectionControlEnum.Free;
                  const entryDirection = convertEnumToNumber(PassagePhysicalDirectionEnum, response.EntryDirection) ?? PassagePhysicalDirectionEnum.Clockwise;

                  const accessPoint: AccessPoint = {
                    AccessPointId: response.AccessPointId,
                    EquipmentId: response.EquipmentId,
                    Name: response.Name,
                    IsActive: response.IsActive,
                    BackendTypeEnum: backendTypeEnum,
                    CameraDevices: normalizeCameraDevices(response.CameraDevices),
                    EntryMode: entryMode,
                    ExitMode: exitMode,
                    EntryDirection: entryDirection,
                    IsSplitScreenMode: response.IsSplitScreenMode,
                    ShowAdvertisement: response.ShowAdvertisement,
                    AdvertisementPosition: convertEnumToNumber(AdvertisementPositionEnum, response.AdvertisementPosition) ?? AdvertisementPositionEnum.Top,
                    ReleaseDurationSeconds: response.ReleaseDurationSeconds ?? 1,
                    GateNumber: response.GateNumber,
                    EdgeId: edgeId,
                    Status: 'unknown' as EdgeStatus,
                    SignalQuality: SIGNAL_QUALITY_OFFLINE,
                    LastTs: DateTime.now().toISO(),
                  };

                  const existing = store.accessPointsEntities()[accessPoint.AccessPointId];
                  if (existing) {
                    patchState(store,
                      updateEntity({ id: accessPoint.AccessPointId, changes: accessPoint }, configAccessPoint),
                      setPropLoaded('configureDevice')
                    );
                  } else {
                    patchState(store,
                      addEntity(accessPoint, configAccessPoint),
                      setPropLoaded('configureDevice')
                    );
                  }
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createAccessPointInitial', error));
                  patchState(store, setPropError('configureDevice', error));
                }
              })
            );
          }),
          catchError((error: HttpErrorResponse) => {
            errorStore.setHttpError(error);
            patchState(store, setPropError('configureDevice', error));
            return EMPTY;
          })
        )),
        catchError((error: HttpErrorResponse) => {
          errorStore.setHttpError(error);
          patchState(store, setPropError('configureDevice', error));
          return EMPTY;
        })
      )),

      updateAccessPointStatus: (accessPointId: string, status: EdgeStatus): void => {
        const existing = store.accessPointsEntities()[accessPointId];
        if (existing) {
          patchState(store, updateEntity({
            id: accessPointId,
            changes: {
              Status: status,
              SignalQuality: status === 'online' ? SIGNAL_QUALITY_ONLINE : SIGNAL_QUALITY_OFFLINE,
              LastTs: DateTime.now().toISO(),
            }
          }, configAccessPoint));
        }
      },

      upsertAccessPoint: (update: Partial<AccessPoint> & { AccessPointId: string }): void => {
        // By id, from the map: `accessPointsEntities()` is an array, and indexing it
        // by id always missed, so an existing point fell to `addEntity`, which skips it.
        const existing = store.accessPointsEntityMap()[update.AccessPointId];
        const merged: AccessPoint = {
          ...existing,
          ...update,
          AccessPointId: update.AccessPointId,
          CameraDevices: update.CameraDevices ?? existing?.CameraDevices ?? [],
          Status: update.Status ?? existing?.Status ?? 'unknown',
          LastTs: update.LastTs ?? DateTime.now().toISO(),
        };

        if (existing) {
          patchState(store, updateEntity({ id: update.AccessPointId, changes: merged }, configAccessPoint));
        } else {
          patchState(store, addEntity(merged, configAccessPoint));
        }
      },

      handleNewEdgeId: (edgeId: string): void => {
        const accessPoints = Object.values(store.accessPointsEntities());
        const existingAccessPoint = accessPoints.find(ap => ap.EdgeId === edgeId);

        if (!existingAccessPoint) {
          (store as any).configureDevice(edgeId);
        }
      },

      upsertEdge: (update: Partial<EdgeItem>): void => {
        const existing = store.edgesEntityMap()[update.EdgeId];
        if (existing) {
          const updated: EdgeItem = {
            ...existing,
            ...update,
            EdgeId: update.EdgeId!,
            Status: update.Status ?? existing.Status,
            LastTs: update.LastTs ?? (update.Status !== undefined || update.ApiKeyKind !== undefined || update.Net !== undefined 
              ? DateTime.now().toISO() 
              : existing.LastTs),
          };
          patchState(store, updateEntity({ id: update.EdgeId, changes: updated }, configEdge));
        } else {
          const status = update.Status ?? 'unknown';
          const newEdge: EdgeItem = {
            EdgeId: update.EdgeId!,
            Status: status,
            SignalQuality: update.SignalQuality ?? (status === 'online' ? SIGNAL_QUALITY_ONLINE : SIGNAL_QUALITY_OFFLINE),
            LastTs: update.LastTs ?? DateTime.now().toISO(),
            ApiKeyKind: update.ApiKeyKind,
            Net: update.Net,
            LastGatePassage: update.LastGatePassage,
          };
          
          patchState(store, addEntity(newEdge, configEdge));
        }
      },

      setHubConnectionStatus: (status: HubConnectionStatus): void => {
        patchState(store, { hubConnectionStatus: status });
      },

      setWizardFlowActive: (isActive: boolean): void => {
        patchState(store, { isWizardFlowActive: isActive });
      },

      startHub: async (): Promise<void> => {
        await hubService.start();
      },

      /**
       * Assina o progresso de cadastro de digital deste equipamento.
       *
       * A ordem é obrigatória: assinar **antes** de disparar o POST, senão as
       * primeiras notificações perdem-se — a preparação do sensor arranca de
       * imediato do lado da placa.
       */
      subscribeEdge: async (edgeId: string): Promise<void> => {
        await hubService.subscribeEdge(edgeId);
      },

      /** Contrapartida do `subscribeEdge`, chamada ao sair da tela. Nunca `stopHub`. */
      unsubscribeEdge: async (edgeId: string): Promise<void> => {
        await hubService.unsubscribeEdge(edgeId);
      },

      /**
       * Força o reenvio de todas as digitais embarcadas para um equipamento.
       * Ação de manutenção — nada é apagado.
       */
      resetDeviceByUser: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('resetDeviceByUser'))),
          switchMap((macAddress) =>
            edgesService.resetDeviceByUser(macAddress).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('resetDeviceByUser')),
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('resetDeviceByUser', error));
                  errorStore.setHttpError(error);
                },
              })
            )
          )
        )
      ),

      stopHub: async (): Promise<void> => {
        await hubService.stop();
      },

      resetReqState: () => {
        patchState(store, setPropInit(
          'getPublicIp',
          'loadInitialDevices',
          'bindEdge',
          'loadAccessPoints',
          'createAccessPointInitial',
          'updateAccessPoint',
          'configureDevice',
          'releaseEdge',
          'evaluateByPersonId',
          'unbindEdge',
          'deleteAccessPoint',
          'loadWebcams',
          'getSpacesAccessPointsByAccessPointId',
          'createSpaceAccessPoint',
          'deleteSpaceAccessPoint',
          'loadBiometricReaders'
        ));
      },

      resetEvaluateByPersonIdState: () => {
        patchState(store, setPropInit('evaluateByPersonId'), { lastEvaluateByPersonIdAccessIntent: null, lastEvaluateByPersonIdLogicalDirection: null });
      },

      isStale: (lastTs: string): boolean => {

        const lastUpdate = getDateTime(lastTs);
        if (!lastUpdate?.isValid) return true;

        return DateTime.now().toMillis() - lastUpdate.toMillis() > 45000;
      },

      releaseEdge: rxMethod<{ edgeId: string; direction: ELogicalDirectionEnum }>(pipe(
        tap(({ direction }) => patchState(store, setPropLoading('releaseEdge'), { lastDirection: direction })),
        switchMap(({ edgeId, direction }) =>
          edgesService.releaseEdge(edgeId, direction).pipe(
            tapResponse({
              next: () => {
                const current = store.lastReleaseByEdgeId ?? {};
                const updated: Record<string, { edgeId: string; direction: 'Entry' | 'Exit'; ts: string }> = {
                  ...current,
                  [edgeId]: {
                    edgeId,
                    direction,
                    ts: DateTime.now().toISO(),
                  } as any,
                } as any;

                patchState(store,
                  { lastReleaseByEdgeId: updated as any },
                  setPropLoaded('releaseEdge')
                );
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('releaseEdge', error));
              }
            })
          )
        )
      )),

      evaluateByPersonId: rxMethod<{ edgeId: string; personId: string; accessIntent: AccessIntentEnum }>(pipe(
        tap(({ accessIntent }) => patchState(store, setPropLoading('evaluateByPersonId'), { lastEvaluateByPersonIdAccessIntent: accessIntent })),
        switchMap(({ edgeId, personId, accessIntent }) => {
          const request: EvaluateByPersonIdRequest = {
            Identifier: personId,
            AccessMethod: AccessMethodEnum.PersonId,
            AccessIntent: accessIntent,
          };

          return edgesService.evaluateByPersonId(edgeId, request).pipe(
            tapResponse({
              next: (response: { Response?: { LogicalDirection?: number } }) => {
                const logicalDirection = response?.Response?.LogicalDirection ?? null;
                patchState(store, setPropLoaded('evaluateByPersonId'), { lastEvaluateByPersonIdLogicalDirection: logicalDirection });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, setPropError('evaluateByPersonId', error));
              }
            })
          );
        })
      )),

      unbindEdge: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('unbindEdge'))),
        switchMap((edgeId) => edgesService.unbindEdge(edgeId).pipe(
          retry({
            count: 2,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: () => {
              patchState(store, setPropLoaded('unbindEdge'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('unbindEdge', error));
            }
          })
        ))
      )),

      deleteAccessPoint: rxMethod<{ accessPointId: string; edgeId?: string }>(pipe(
        tap(() => patchState(store, setPropLoading('deleteAccessPoint'))),
        switchMap(({ accessPointId, edgeId }) => {
          const trimmedEdgeId = edgeId?.trim();
          const unbind$ = trimmedEdgeId ? edgesService.unbindEdge(trimmedEdgeId) : of('');

          return unbind$.pipe(
            switchMap(() => edgesService.deleteAccessPoint(accessPointId)),
            tapResponse({
              next: () => {
                patchState(store,
                  removeEntity(accessPointId, configAccessPoint),
                  setPropLoaded('deleteAccessPoint')
                );
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('deleteAccessPoint', error));
              }
            })
          );
        })
      )),

      loadWebcams: rxMethod<string | null>(pipe(
        tap(() => patchState(store, setPropLoading('loadWebcams'))),
        switchMap((edgeId) => {
          if (!edgeId?.trim()) {
            patchState(store, { webcams: [] }, setPropLoaded('loadWebcams'));
            return EMPTY;
          }
          return edgesService.getWebcams(edgeId).pipe(
            retry({
              count: 2,
              delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 5000)),
            }),
            tapResponse({
              next: (webcams) => {
                patchState(store, { webcams: webcams ?? [] }, setPropLoaded('loadWebcams'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { webcams: [] }, setPropError('loadWebcams', error));
              }
            })
          );
        })
      )),

      /**
       * Deactivates the access-point webcams matching the browser's video devices so
       * `getUserMedia` can open them — the edge agent holds the device exclusively while
       * an access point is running.
       *
       * Reads the access points already in the store, so the caller must let
       * `loadAccessPoints` settle first; resolving against an empty store releases nothing
       * and the device stays locked.
       *
       * O comando vale para o edge inteiro — a API não recebe nome de dispositivo —, então
       * as referências resolvidas viram uma requisição por edge distinto.
       *
       * `deviceAction` é best-effort: um edge que falha sai do resultado em vez de abortar os
       * outros, e nenhum alerta é levantado — isto roda como faxina por trás de uma ação de UI
       * que já reporta o próprio desfecho.
       */
      releaseWebcamsForBrowser: rxMethod<string[]>(pipe(
        tap(() => patchState(store, setPropLoading('releaseWebcamsForBrowser'))),
        concatMap((deviceLabels: string[]) => {
          const targets = resolveWebcamsToRelease(
            Object.values(store.accessPointsEntities()),
            deviceLabels
          );

          if (targets.length === 0) {
            patchState(store, { webcamsReleasedForBrowser: [] }, setPropLoaded('releaseWebcamsForBrowser'));
            return EMPTY;
          }

          const edgeIds = Array.from(new Set(targets.map((target) => target.edgeId)));

          return forkJoin(
            edgeIds.map((edgeId) =>
              edgesService.deviceAction(edgeId, EEdgeDeviceAction.Deactivate).pipe(
                map(() => edgeId),
                catchError(() => of(null))
              )
            )
          ).pipe(
            tapResponse({
              next: (results) => {
                const releasedEdgeIds = new Set(results.filter((edgeId): edgeId is string => edgeId !== null));
                patchState(store, {
                  webcamsReleasedForBrowser: targets.filter((target) =>
                    releasedEdgeIds.has(target.edgeId)
                  ),
                }, setPropLoaded('releaseWebcamsForBrowser'));
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, setPropError('releaseWebcamsForBrowser', error));
              }
            })
          );
        })
      )),

      /**
       * Gives the previously released webcams back to their access points.
       *
       * Deliberately not an `rxMethod`: this runs while the caller is being destroyed, and
       * an `rxMethod` subscription torn down with the component would abort the request and
       * leave the device released — the access point would stay blind until the next restart.
       */
      restoreWebcamsForAccess: (): void => {
        const pairs = store.webcamsReleasedForBrowser();
        patchState(store, { webcamsReleasedForBrowser: [] }, setPropInit('releaseWebcamsForBrowser'));

        const edgeIds = Array.from(new Set(pairs.map(({ edgeId }) => edgeId)));
        edgeIds.forEach((edgeId) => {
          edgesService.deviceAction(edgeId, EEdgeDeviceAction.Activate).subscribe({
            error: (error) =>
              console.warn(`Failed to restore cameras for access on edge ${edgeId}`, error),
          });
        });
      },
    }
  }),
  withReqState(
    'getPublicIp',
    'loadInitialDevices',
    'bindEdge',
    'loadAccessPoints',
    'createAccessPointInitial',
    'updateAccessPoint',
    'configureDevice',
    'releaseEdge',
    'evaluateByPersonId',
    'unbindEdge',
    'deleteAccessPoint',
    'loadWebcams',
    'getSpacesAccessPointsByAccessPointId',
    'createSpaceAccessPoint',
    'deleteSpaceAccessPoint',
    'loadBiometricReaders',
    'releaseWebcamsForBrowser',
    'resetDeviceByUser'
  ),
  withHooks((store) => {
    const hubService = inject(AccessPointEdgeHubService);
    const subscriptions: Subscription[] = [];
    let staleCheckInterval: ReturnType<typeof setInterval> | null = null;
    const addSubscription = (subscription: Subscription) => subscriptions.push(subscription);

    /**
     * Assina um observable do hub isolando o handler do contexto reativo de quem
     * provocou a emissão.
     *
     * O hub emite **de forma síncrona**: `start()`, `stop()` e o
     * `syncStatusFromConnection()` dos handlers de mensagem chamam `next()` na
     * pilha do chamador. Sem o `untracked`, o `patchState` daqui herda o
     * `activeConsumer` desse chamador — e se for um `computed()` ou a avaliação
     * de um template (ambos com `consumerAllowSignalWrites: false`), o Angular
     * recusa a escrita com NG0600 e derruba a aplicação. Foi o que aconteceu na
     * queda do WebSocket (1006) que dispara `onclose`/`onreconnecting`.
     */
    const subscribeFromHub = <T>(
      source: Observable<T>,
      handler: (value: T) => void
    ): void => {
      addSubscription(source.subscribe((value) => untracked(() => handler(value))));
    };

    return {
      onInit: () => {
        subscribeFromHub(hubService.hubStatus$, (status) => store.setHubConnectionStatus(status));

        subscribeFromHub(hubService.edgeOnline$, ({ edgeId, apiKeyKind }) => {
          store.upsertEdge({
            EdgeId: edgeId,
            Status: 'online',
            SignalQuality: SIGNAL_QUALITY_ONLINE,
            ApiKeyKind: apiKeyKind,
            LastTs: DateTime.now().toISO(),
          });
        });

        subscribeFromHub(hubService.edgeOffline$, ({ edgeId }) => {
          store.upsertEdge({
            EdgeId: edgeId,
            Status: 'offline',
            SignalQuality: SIGNAL_QUALITY_OFFLINE,
            LastTs: DateTime.now().toISO(),
          });
        });

        subscribeFromHub(hubService.presenceSnapshot$, ({ items }) => {
          items?.forEach(({ edgeId, signalQuality, online }) => {
            store.upsertEdge({
              EdgeId: edgeId,
              Status: signalQuality !== ESignalQuality.Offline && online ? 'online' : 'offline',
              SignalQuality: ESignalQuality[signalQuality] as keyof typeof ESignalQuality,
              LastTs: DateTime.now().toISO(),
            });
          })
        });

        subscribeFromHub(hubService.heartbeat$, ({ edgeId, signalQuality, accessPointName }) => {
          store.upsertEdge({
            EdgeId: edgeId,
            Status: signalQuality !== ESignalQuality.Offline ? 'online' : 'offline',
            SignalQuality: ESignalQuality[signalQuality] as keyof typeof ESignalQuality,
            AccessPointName: accessPointName,
            LastTs: DateTime.now().toISO(),
          });
        });

        subscribeFromHub(hubService.networkInfo$, (event: AccessPointEdgeNetworkInfoEvent) => {
          store.upsertEdge({
            EdgeId: event.edgeId,
            Status: 'online',
            SignalQuality: SIGNAL_QUALITY_ONLINE,
            LastTs: DateTime.now().toISO(),
          });
        });

        subscribeFromHub(hubService.gatePassage$, (event: AccessPointEdgeGatePassageEvent) => {
          store.upsertEdge({
            EdgeId: event.edgeId,
            LastGatePassage: event.lastGatePassage,
            Status: 'online',
            SignalQuality: SIGNAL_QUALITY_ONLINE,
            LastTs: DateTime.now().toISO(),
          });
        });

        subscribeFromHub(hubService.accessEvalResult$, (event: AccessPointEdgeAccessEvalResultEvent) => {
          const current = store.lastAccessEvalResultByEdgeId();
          patchState(store, {
            lastAccessEvalResultByEdgeId: {
              ...current,
              [event.edgeId]: event.accessEvalResult,
            },
          });
          store.upsertEdge({
            EdgeId: event.edgeId,
            Status: 'online',
            SignalQuality: SIGNAL_QUALITY_ONLINE,
            LastTs: DateTime.now().toISO(),
          });
        });
      },
      onDestroy: () => {
        subscriptions.forEach((subscription) => subscription.unsubscribe());
        subscriptions.length = 0;
      }
    };
  })
);
